import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, remapUVsToWorldSpace, setMaxAnisotropy } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const _isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
// Viewer-right 90° rotation from the original front-right framing
// (scene appears to rotate right → camera orbits CCW around Y as seen from above).
const DEFAULT_CAM = _isMobile ? { x: -1.25, y: 0.85, z: 1.0 } : { x: -1.35, y: 0.95, z: 1.1 };
const SCALE = 0.008;
const BACK_TILT_DEG = 12; // back cushion leans back 12° from vertical
const FLAP_DEPTH_CM = 4; // 4 cm flap: 2 cm bridges the gap, 2 cm tucks behind the back-cushion front piping
const GAP_BETWEEN_CM = 1; // 1 cm gap between seat back edge and back cushion front face (closer so no visible sliver)
const DIM_INSET_CM = 15; // dotted dimension lines sit 15 cm inside the edges
const DIM_LERP = 0.12; // smoothing factor for dimension animation
const BACK_Y_DROP = 0.0; // back cushion bottom sits flush with the seat top (raised so flap connects near the top corner)
const FLAP_BACK_DROP = 0.012; // ~12 mm total drop on the flap's back end so it still tucks under the 15 mm piping radius
const CORNER_RADIUS_WORLD = 0.012; // 15 mm fixed radius (15 * SCALE = 0.012)

// Build a flat rounded-rectangle extrusion with FIXED radius. Used for seat (vertical=false,
// extrude axis = +Y after rotation) and back cushion (vertical=true, extrude axis = +Z).
function buildRoundedFaceGeometry(faceW, faceH, thickness, vertical) {
  const r = Math.min(CORNER_RADIUS_WORLD, faceW * 0.45, faceH * 0.45);
  const w = Math.max(faceW, r * 2 + 0.0001);
  const h = Math.max(faceH, r * 2 + 0.0001);
  const t = Math.max(thickness, 0.001);
  const s = new THREE.Shape();
  s.moveTo(-w / 2 + r, -h / 2);
  s.lineTo(w / 2 - r, -h / 2);
  s.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + r);
  s.lineTo(w / 2, h / 2 - r);
  s.quadraticCurveTo(w / 2, h / 2, w / 2 - r, h / 2);
  s.lineTo(-w / 2 + r, h / 2);
  s.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - r);
  s.lineTo(-w / 2, -h / 2 + r);
  s.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + r, -h / 2);
  const geo = new THREE.ExtrudeGeometry(s, { depth: t, bevelEnabled: false, curveSegments: 6 });
  geo.translate(0, 0, -t / 2); // centre on extrude axis
  if (!vertical) geo.rotateX(-Math.PI / 2); // seat: extrude axis → +Y
  remapUVsToWorldSpace(geo, 1, 1, 1, 0.8);
  return geo;
}

const HingedCushion3DPreview = ({
  width = 50, seatDepth = 50, backHeight = 100, thickness = 7.5,
  rawWidth, rawSeatDepth, rawBackHeight, rawThickness, unit = 'cm',
  fabricColor, fabricImageUrl, fabricZoom = 3, showZip = true, activeField = null,
  pipingOption = 'none', pipingColor = null, pipingImageUrl = null,
}) => {
  const containerRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const sceneRef = useRef(null);
  const seatRef = useRef(null);
  const backGroupRef = useRef(null);
  const backMeshRef = useRef(null);
  const flapRef = useRef(null);
  const zipGroupRef = useRef(null);
  const seatPipingTopRef = useRef(null);
  const seatPipingBottomRef = useRef(null);
  const backPipingFrontRef = useRef(null);
  const backPipingBackRef = useRef(null);
  const dimLabelsRef = useRef({ A: null, B: null, C: null, D: null });
  const dimLinesRef = useRef(null);
  const animFrameRef = useRef(null);
  const initializedRef = useRef(false);
  const resetCameraRef = useRef(false);
  const containerWidthRef = useRef(600);
  const activeFieldRef = useRef(activeField);
  // Target + current dim refs — we lerp currentDims toward targetDims each frame so
  // dimension changes animate smoothly (same technique as Premium Seat).
  const targetDimsRef = useRef(null);
  const currentDimsRef = useRef(null);

  // World units (targets)
  const sA = Math.max(width * SCALE, 0.1);
  const sB = Math.max(seatDepth * SCALE, 0.1);
  const sC = Math.max(backHeight * SCALE, 0.1);
  const sD = Math.max(thickness * SCALE, 0.03);
  const color = fabricColor || '#5a8a5a';

  const fabricImageUrlRef = useRef(fabricImageUrl);
  const colorRef = useRef(color);
  const fabricZoomRef = useRef(fabricZoom);
  const pipingOptionRef = useRef(pipingOption);

  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);
  useEffect(() => { fabricImageUrlRef.current = fabricImageUrl; colorRef.current = color; }, [fabricImageUrl, color]);
  useEffect(() => { fabricZoomRef.current = fabricZoom; }, [fabricZoom]);
  useEffect(() => { pipingOptionRef.current = pipingOption; }, [pipingOption]);

  // Apply piping color/texture to all four piping meshes
  useEffect(() => {
    const meshes = [seatPipingTopRef.current, seatPipingBottomRef.current, backPipingFrontRef.current, backPipingBackRef.current].filter(Boolean);
    if (!meshes.length) return;
    if (!pipingOption || pipingOption === 'none') { setPipingLoading(false); return; }
    const pColor = pipingOption === 'same' ? (fabricColor || '#5a8a5a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let remaining = meshes.length;
    const done = () => { remaining -= 1; if (remaining <= 0) setPipingLoading(false); };
    meshes.forEach(m => applyFabricTexture(m, pImageUrl, pColor, fabricZoom, needsLoad ? done : null));
  }, [pipingOption, pipingColor, pipingImageUrl, fabricColor, fabricImageUrl, fabricZoom]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    initializedRef.current = true;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    containerWidthRef.current = rect.width;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, rect.width / rect.height, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(0, 0.3, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(rect.width, rect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    setMaxAnisotropy(renderer);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.2;
    controls.maxDistance = 5;
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    controls.target.set(0, 0.3, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controlsRef.current = controls;

    scene.add(new THREE.AmbientLight(0xffffff, 0.65));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.1);
    dirLight.position.set(3, 5, 3);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(1024, 1024);
    scene.add(dirLight);
    const fill = new THREE.DirectionalLight(0xffffff, 0.4);
    fill.position.set(-2, 3, -1);
    scene.add(fill);

    const ground = new THREE.Mesh(new THREE.PlaneGeometry(6, 6), new THREE.ShadowMaterial({ opacity: 0.15 }));
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    ground.receiveShadow = true;
    scene.add(ground);

    // Seat cushion — flat extrusion with rounded plan-view corners (15mm fixed radius).
    // Geometry is rebuilt each frame during animation so the radius stays constant.
    const seatMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.85, metalness: 0.02 });
    const seatGeo = buildRoundedFaceGeometry(sA, sB, sD, false);
    const seat = new THREE.Mesh(seatGeo, seatMat);
    seat.castShadow = true;
    seat.receiveShadow = true;
    scene.add(seat);
    seatRef.current = seat;

    // Back cushion — front/back A×C faces with 15mm fixed radius, rebuilt per frame.
    const backGroup = new THREE.Group();
    scene.add(backGroup);
    backGroupRef.current = backGroup;
    const backMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.85, metalness: 0.02 });
    const backGeo = buildRoundedFaceGeometry(sA, sC, sD, true);
    const back = new THREE.Mesh(backGeo, backMat);
    back.castShadow = true;
    back.receiveShadow = true;
    backGroup.add(back);
    backMeshRef.current = back;

    // Flap (unit cube, scaled per-frame)
    const flapMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.9, metalness: 0.02 });
    const flapGeo = new THREE.BoxGeometry(1, 1, 1);
    remapUVsToWorldSpace(flapGeo, 1, 1, 1, 0.8);
    const flap = new THREE.Mesh(flapGeo, flapMat);
    flap.castShadow = true;
    scene.add(flap);
    flapRef.current = flap;

    // Initialise target + current dims so the first render is correctly sized
    targetDimsRef.current = { sA, sB, sC, sD };
    currentDimsRef.current = { sA, sB, sC, sD };

    // Zip group
    const zipGroup = new THREE.Group();
    scene.add(zipGroup);
    zipGroupRef.current = zipGroup;

    // Piping tubes — around top/bottom of seat, and around front/back of back cushion.
    // Matches Premium Seat style. Geometry rebuilt per-frame when enabled.
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const seatPipingTop = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    const seatPipingBottom = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    seatPipingTop.visible = false; seatPipingBottom.visible = false;
    scene.add(seatPipingTop); scene.add(seatPipingBottom);
    seatPipingTopRef.current = seatPipingTop;
    seatPipingBottomRef.current = seatPipingBottom;

    const backPipingFront = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    const backPipingBack = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    backPipingFront.visible = false; backPipingBack.visible = false;
    backGroup.add(backPipingFront); backGroup.add(backPipingBack);
    backPipingFrontRef.current = backPipingFront;
    backPipingBackRef.current = backPipingBack;

    // Dimension lines
    const lineMatA = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const lineMatB = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const lineMatC = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const lineMatD = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const dashGeoA = new THREE.BoxGeometry(0.005, 0.003, 0.003);
    const dashGeoB = new THREE.BoxGeometry(0.003, 0.003, 0.005);
    const dashGeoC = new THREE.BoxGeometry(0.003, 0.005, 0.003);
    const arrowGeo = new THREE.ConeGeometry(0.007, 0.015, 6);

    const aDashes = [], bDashes = [], cDashes = [], dDashes = [];
    for (let i = 0; i < 24; i++) { const m = new THREE.Mesh(dashGeoA, lineMatA); scene.add(m); aDashes.push(m); }
    for (let i = 0; i < 16; i++) { const m = new THREE.Mesh(dashGeoB, lineMatB); scene.add(m); bDashes.push(m); }
    for (let i = 0; i < 22; i++) { const m = new THREE.Mesh(dashGeoC, lineMatC); scene.add(m); cDashes.push(m); }
    for (let i = 0; i < 5; i++) { const m = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.003), lineMatD); scene.add(m); dDashes.push(m); }

    const aA1 = new THREE.Mesh(arrowGeo, lineMatA); scene.add(aA1);
    const aA2 = new THREE.Mesh(arrowGeo, lineMatA); scene.add(aA2);
    const bA1 = new THREE.Mesh(arrowGeo, lineMatB); scene.add(bA1);
    const bA2 = new THREE.Mesh(arrowGeo, lineMatB); scene.add(bA2);
    const cA1 = new THREE.Mesh(arrowGeo, lineMatC); scene.add(cA1);
    const cA2 = new THREE.Mesh(arrowGeo, lineMatC); scene.add(cA2);

    dimLinesRef.current = { aDashes, bDashes, cDashes, dDashes, aA1, aA2, bA1, bA2, cA1, cA2, lineMatA, lineMatB, lineMatC, lineMatD };

    sceneRef.current = scene;
    resetCameraRef.current = true;

    // Helper: update dashed dimension lines to a given set of current dims (called per-frame)
    const updateDimLines = (cA, cB, cC, cD) => {
      const dl = dimLinesRef.current;
      if (!dl) return;
      const insetM = DIM_INSET_CM * SCALE;
      const flapDepth = FLAP_DEPTH_CM * SCALE;
      const halfA = cA / 2;
      const halfB = cB / 2;
      const topY = cD + 0.002;
      const safeInset = (dim) => Math.min(insetM, dim * 0.5 - 0.01);

      // Helper: each arrow cone is 15mm long with apex pointing outward, so offset the
      // arrow position inward by half its length so the TIP lands exactly on the edge.
      const ARROW_LEN = 0.015;
      const ARROW_HALF = ARROW_LEN / 2;

      // A line: spans full width on the seat top, 15cm inset from the front edge
      const aZ = halfB - safeInset(cB);
      const aDashStart = -halfA + ARROW_LEN;
      const aDashEnd = halfA - ARROW_LEN;
      const aDashLen = aDashEnd - aDashStart;
      for (let i = 0; i < dl.aDashes.length; i++) {
        const t = (i + 0.5) / dl.aDashes.length;
        dl.aDashes[i].position.set(aDashStart + aDashLen * t, topY, aZ);
      }
      dl.aA1.position.set(-halfA + ARROW_HALF, topY, aZ); dl.aA1.rotation.set(0, 0, Math.PI / 2);
      dl.aA2.position.set(halfA - ARROW_HALF, topY, aZ); dl.aA2.rotation.set(0, 0, -Math.PI / 2);

      // B line: on the seat top, 15cm inset from the right edge, stops a flap-depth short of the back edge
      const bX = halfA - safeInset(cA);
      const bLineStart = -halfB + flapDepth * 0.35; // extend closer to the flap edge
      const bLineEnd = halfB;                 // front end at the front edge
      const bDashStart = bLineStart + ARROW_LEN;
      const bDashEnd = bLineEnd - ARROW_LEN;
      const bDashLen = Math.max(bDashEnd - bDashStart, 0.001);
      for (let i = 0; i < dl.bDashes.length; i++) {
        const t = (i + 0.5) / dl.bDashes.length;
        dl.bDashes[i].position.set(bX, topY, bDashStart + bDashLen * t);
      }
      dl.bA1.position.set(bX, topY, bLineStart + ARROW_HALF); dl.bA1.rotation.set(-Math.PI / 2, 0, 0);
      dl.bA2.position.set(bX, topY, bLineEnd - ARROW_HALF); dl.bA2.rotation.set(Math.PI / 2, 0, 0);

      // C line: on the back cushion front face, 15cm inset from the right edge, stops a flap-depth above the bottom
      const tilt = (BACK_TILT_DEG * Math.PI) / 180;
      const cX = halfA - safeInset(cA);
      const cLineStart = flapDepth * 0.35; // extend closer to the flap edge (bottom of C)
      const cLineEnd = cC;           // top edge of back cushion
      const cDashStart = cLineStart + ARROW_LEN;
      const cDashEnd = cLineEnd - ARROW_LEN;
      const cDashLen = Math.max(cDashEnd - cDashStart, 0.001);
      const frontOffset = 0.002;
      const cBaseY = cD - BACK_Y_DROP;
      const cBaseZ = -cB / 2 - GAP_BETWEEN_CM * SCALE + frontOffset;
      for (let i = 0; i < dl.cDashes.length; i++) {
        const t = (i + 0.5) / dl.cDashes.length;
        const localY = cDashStart + cDashLen * t;
        dl.cDashes[i].position.set(cX, cBaseY + Math.cos(tilt) * localY, cBaseZ - Math.sin(tilt) * localY);
      }
      // Arrow tips land at cLineStart and cLineEnd exactly.
      const cA1Y = cLineStart + ARROW_HALF;
      const cA2Y = cLineEnd - ARROW_HALF;
      dl.cA1.position.set(cX, cBaseY + Math.cos(tilt) * cA1Y, cBaseZ - Math.sin(tilt) * cA1Y);
      dl.cA1.rotation.set(Math.PI + tilt, 0, 0);
      dl.cA2.position.set(cX, cBaseY + Math.cos(tilt) * cA2Y, cBaseZ - Math.sin(tilt) * cA2Y);
      dl.cA2.rotation.set(tilt, 0, 0);

      for (let i = 0; i < dl.dDashes.length; i++) dl.dDashes[i].visible = false;
    };

    // Helper: build a closed rounded-rectangle tube geometry (used for piping on both cushions)
    const buildPipingTube = (faceW, faceH, cornerR) => {
      const r = Math.min(cornerR, faceW * 0.45, faceH * 0.45);
      const hw = faceW / 2;
      const hh = faceH / 2;
      const tubeRadius = 0.003;
      const inset = tubeRadius * 0.25;
      const ihw = hw - inset;
      const ihh = hh - inset;
      const arcSegs = 16;
      const pts = [];
      const addArc = (cx, cy, startAngle) => {
        for (let i = 0; i <= arcSegs; i++) {
          const a = startAngle + (Math.PI / 2) * (i / arcSegs);
          pts.push(new THREE.Vector3(cx + r * Math.cos(a), cy + r * Math.sin(a), 0));
        }
      };
      // 4 corner arcs (TR, TL, BL, BR) connected by straight segments (implicit)
      addArc(ihw - r, ihh - r, 0);
      addArc(-(ihw - r), ihh - r, Math.PI / 2);
      addArc(-(ihw - r), -(ihh - r), Math.PI);
      addArc(ihw - r, -(ihh - r), 3 * Math.PI / 2);
      class LinearClosedCurve extends THREE.Curve {
        constructor(points) { super(); this.pts = points; }
        getPoint(t) {
          const n = this.pts.length;
          const f = t * n;
          const i = Math.floor(f) % n;
          const j = (i + 1) % n;
          const frac = f - Math.floor(f);
          return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[j], frac);
        }
      }
      return new THREE.TubeGeometry(new LinearClosedCurve(pts), 256, tubeRadius, 6, true);
    };
    // Expose helper to refs so the update effect + animate can use it
    seatPipingTopRef.current.__buildTube = buildPipingTube;

    const targetCamPos = new THREE.Vector3();
    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      controls.update();

      // ── Smoothly lerp current dims toward target dims and update meshes each frame ──
      const tgt = targetDimsRef.current;
      const cur = currentDimsRef.current;
      if (tgt && cur) {
        cur.sA += (tgt.sA - cur.sA) * DIM_LERP;
        cur.sB += (tgt.sB - cur.sB) * DIM_LERP;
        cur.sC += (tgt.sC - cur.sC) * DIM_LERP;
        cur.sD += (tgt.sD - cur.sD) * DIM_LERP;

        const cA = cur.sA, cB = cur.sB, cC = cur.sC, cD = cur.sD;
        const flapDepth = FLAP_DEPTH_CM * SCALE;
        const gapDepth = GAP_BETWEEN_CM * SCALE;
        const flapW = Math.max(cA - 4.0 * SCALE, 0.01); // 20mm inset each side (4cm total)
        const tiltRad = (BACK_TILT_DEG * Math.PI) / 180;
        // Flap angled down from seat back-top corner to well below the back-cushion front piping,
        // with the full drop spread across the flap's full 4 cm depth.
        const flapSlopeRad = Math.atan2(BACK_Y_DROP + FLAP_BACK_DROP, flapDepth);

        if (seatRef.current) {
          // Rebuild geometry each frame so the 15mm corner radius stays fixed (not scaled)
          seatRef.current.geometry.dispose();
          seatRef.current.geometry = buildRoundedFaceGeometry(cA, cB, cD, false);
          seatRef.current.scale.set(1, 1, 1);
          seatRef.current.position.set(0, cD / 2, 0);
        }
        if (backMeshRef.current) {
          backMeshRef.current.geometry.dispose();
          backMeshRef.current.geometry = buildRoundedFaceGeometry(cA, cC, cD, true);
          backMeshRef.current.scale.set(1, 1, 1);
          backMeshRef.current.position.set(0, cC / 2, -cD / 2);
        }
        if (backGroupRef.current) {
          backGroupRef.current.position.set(0, cD - BACK_Y_DROP, -cB / 2 - gapDepth);
          backGroupRef.current.rotation.set(-tiltRad, 0, 0);
        }
        if (flapRef.current) {
          flapRef.current.scale.set(flapW, 0.004, flapDepth);
          // Flap front stays at the seat back-top corner (z=-cB/2). The flap extends
          // flapDepth (4 cm) toward -Z, so the back end sits 2 cm behind the back
          // cushion front face — tucked under the front-bottom piping.
          // The slope is computed over the full flapDepth so the back end drops by
          // (BACK_Y_DROP + FLAP_BACK_DROP) below the seat top.
          const flapMidY = cD - (BACK_Y_DROP + FLAP_BACK_DROP) / 2;
          flapRef.current.position.set(0, flapMidY, -cB / 2 - flapDepth / 2);
          flapRef.current.rotation.set(flapSlopeRad, 0, 0);
        }

        // Rebuild piping tubes every frame while visible so they track the cushion dims
        const pipingOn = pipingOptionRef.current && pipingOptionRef.current !== 'none';
        const spt = seatPipingTopRef.current, spb = seatPipingBottomRef.current;
        const bpf = backPipingFrontRef.current, bpb = backPipingBackRef.current;
        if (spt && spb) {
          spt.visible = !!pipingOn; spb.visible = !!pipingOn;
          if (pipingOn && spt.__buildTube) {
            // Seat top/bottom — rounded rectangle in XZ plane around (0, ±cD/2, 0)
            const seatTube = spt.__buildTube(cA, cB, CORNER_RADIUS_WORLD);
            spt.geometry.dispose(); spb.geometry.dispose();
            spt.geometry = seatTube; spb.geometry = seatTube.clone();
            // Tube is built in XY plane — rotate into XZ (horizontal)
            spt.rotation.set(-Math.PI / 2, 0, 0);
            spb.rotation.set(-Math.PI / 2, 0, 0);
            spt.position.set(0, cD, 0);
            spb.position.set(0, 0, 0);
          }
        }
        if (bpf && bpb && spt && spt.__buildTube) {
          bpf.visible = !!pipingOn; bpb.visible = !!pipingOn;
          if (pipingOn) {
            // Back front/back — rounded rectangle in XY plane around z = 0 (front) and z = -cD (back)
            const backTube = spt.__buildTube(cA, cC, CORNER_RADIUS_WORLD);
            bpf.geometry.dispose(); bpb.geometry.dispose();
            bpf.geometry = backTube; bpb.geometry = backTube.clone();
            bpf.rotation.set(0, 0, 0);
            bpb.rotation.set(0, 0, 0);
            // Piping sits on the actual front (z=0 local) and back (z=-cD local) faces, at cushion centre
            bpf.position.set(0, cC / 2, 0);
            bpb.position.set(0, cC / 2, -cD);
          }
        }
        // Update dashed dimension lines to match the current dims
        updateDimLines(cA, cB, cC, cD);
      }

      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const dl = dimLinesRef.current;
        if (dl) Object.values(dl).forEach(v => {
          if (Array.isArray(v)) v.forEach(m => { if (m && m.visible !== undefined) m.visible = false; });
          else if (v && v.visible !== undefined) v.visible = false;
        });
        if (zipGroupRef.current) zipGroupRef.current.visible = false;
      }
      renderer.render(scene, camera);
      positionLabels();
      if (resetCameraRef.current) {
        const isMobile = containerWidthRef.current < 500;
        if (isMobile) {
          // Frame the full scene (seat + tilted back; flap sits on top of the seam) — uses TARGET dims.
          // Use FOV-based fit so the camera auto-zooms out when the back cushion is tall.
          const tiltRad = (BACK_TILT_DEG * Math.PI) / 180;
          const sceneWidth = sA;
          const backProjectedDepth = sC * Math.sin(tiltRad) + sD * Math.cos(tiltRad);
          const sceneDepth = sB + backProjectedDepth;
          const sceneHeight = sD + sC * Math.cos(tiltRad) + sD * Math.sin(tiltRad);
          const containerH = rendererRef.current ? rendererRef.current.domElement.clientHeight : 400;
          const containerW = containerWidthRef.current || 400;
          const aspect = containerW / Math.max(containerH, 1);
          const fovRad = (35 / 2) * Math.PI / 180;
          const verticalFit = (sceneHeight * 0.5) / Math.tan(fovRad);
          const horizontalFit = (Math.max(sceneWidth, sceneDepth) * 0.5) / (Math.tan(fovRad) * aspect);
          const depthBuffer = sceneDepth * 0.35;
          const sCExcess = Math.max(0, sC - 0.52);
          const margin = 1.16 + sCExcess * 0.35;
          const dist = Math.max(verticalFit, horizontalFit) * margin + depthBuffer;
          const camScale = 1.4;
          const targetXOffset = 0.12;
          const targetYOffset = -0.10;
          const dirLen = Math.sqrt(DEFAULT_CAM.x ** 2 + DEFAULT_CAM.y ** 2 + DEFAULT_CAM.z ** 2) || 1;
          const nx = DEFAULT_CAM.x / dirLen, ny = DEFAULT_CAM.y / dirLen, nz = DEFAULT_CAM.z / dirLen;
          const scaleFactor = dist / camScale;
          targetCamPos.set(
            nx * dirLen * scaleFactor + targetXOffset,
            ny * dirLen * scaleFactor + targetYOffset,
            nz * dirLen * scaleFactor
          );
          camera.position.lerp(targetCamPos, 0.15);
          const targetY = sD + sceneHeight * 0.35 + targetYOffset;
          const targetZ = -sB / 2;
          const tgt2 = new THREE.Vector3(targetXOffset, targetY, targetZ);
          controls.target.lerp(tgt2, 0.15);
        } else {
          // Desktop: exact user-tuned pose.
          targetCamPos.set(-1.1022, 0.6451, 1.0555);
          camera.position.lerp(targetCamPos, 0.15);
          controls.target.lerp(new THREE.Vector3(0.053, 0.1946, -0.1563), 0.15);
        }
        if (camera.position.distanceTo(targetCamPos) < 0.02) { resetCameraRef.current = false; controls.saveState(); }
      }
    };

    const positionLabels = () => {
      const dl = dimLinesRef.current;
      if (!dl) return;
      const af = activeFieldRef.current;
      const YELLOW = 0xffd700;
      dl.lineMatA.color.set(af === 'width' ? YELLOW : 0xffffff);
      dl.lineMatB.color.set(af === 'seatDepth' ? YELLOW : 0xffffff);
      dl.lineMatC.color.set(af === 'backHeight' ? YELLOW : 0xffffff);
      dl.lineMatD.color.set(af === 'thickness' ? YELLOW : 0xffffff);

      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';
      const cr = container.getBoundingClientRect();
      const proj = (wp) => { const v = wp.clone().project(camera); return { x: (v.x * 0.5 + 0.5) * cr.width, y: (-v.y * 0.5 + 0.5) * cr.height, visible: v.z < 1 }; };

      const applyLabel = (el, wp, field) => {
        if (!el) return;
        const p = proj(wp);
        Object.assign(el.style, { left: p.x + 'px', top: p.y + 'px', opacity: p.visible ? '1' : '0',
          backgroundColor: af === field ? activeBg : defaultBg, color: af === field ? activeText : defaultText });
      };
      // Use CURRENT (lerped) dims so labels follow the smooth cushion animation
      const cur = currentDimsRef.current || { sA, sB, sC, sD };
      const cA = cur.sA, cB = cur.sB, cC = cur.sC, cD = cur.sD;
      // A label sits directly ABOVE its dotted line (line is 15cm inset from the front edge on the seat top)
      const aLabelZ = cB / 2 - Math.min(DIM_INSET_CM * SCALE, cB * 0.5 - 0.01);
      applyLabel(dimLabelsRef.current.A, new THREE.Vector3(0, cD + 0.04, aLabelZ), 'width');
      // B label — sits ABOVE the seat top, to the LEFT of the B dotted line.
      const bLineXWorld = cA / 2 - Math.min(DIM_INSET_CM * SCALE, cA * 0.5 - 0.01);
      const bLabelX = bLineXWorld - 0.045; // ~5cm to the left of the B line
      const bLabelZ = -cB / 2 + FLAP_DEPTH_CM * SCALE + 0.08; // moved forward along +Z
      applyLabel(dimLabelsRef.current.B, new THREE.Vector3(bLabelX, cD + 0.04, bLabelZ), 'seatDepth');
      const tilt = (BACK_TILT_DEG * Math.PI) / 180;
      const cLocalY = cC / 2;
      // C label sits to the LEFT of its dotted line on the back cushion front face
      const cLabelX = cA / 2 - Math.min(DIM_INSET_CM * SCALE, cA * 0.5 - 0.01) - 0.05;
      applyLabel(
        dimLabelsRef.current.C,
        new THREE.Vector3(cLabelX, cD - BACK_Y_DROP + Math.cos(tilt) * cLocalY, -cB / 2 - GAP_BETWEEN_CM * SCALE - Math.sin(tilt) * cLocalY),
        'backHeight'
      );
      applyLabel(dimLabelsRef.current.D, new THREE.Vector3(-cA / 2 - 0.02, cD / 2, cB / 2 + 0.02), 'thickness');
    };
    animate();

    const handleResize = () => {
      const r = container.getBoundingClientRect();
      containerWidthRef.current = r.width;
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
      renderer.setSize(r.width, r.height);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update target dims when inputs change — lerped toward in animate() for smooth animation
  useEffect(() => {
    targetDimsRef.current = { sA, sB, sC, sD };
    // Ensure current dims are initialised (first render)
    if (!currentDimsRef.current) currentDimsRef.current = { sA, sB, sC, sD };
    resetCameraRef.current = true;
  }, [sA, sB, sC, sD]);

  // Update fabric textures
  useEffect(() => {
    const apply = (mesh) => {
      if (!mesh) return;
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(mesh, fabricImageUrl, color, fabricZoom, () => setFabricLoading(false));
    };
    apply(seatRef.current);
    apply(backMeshRef.current);
    apply(flapRef.current);
  }, [color, fabricImageUrl, fabricZoom]);

  // Update zip lines on width edge of both cushions near the flap
  useEffect(() => {
    const zg = zipGroupRef.current;
    if (!zg) return;
    while (zg.children.length) {
      const ch = zg.children[0];
      if (ch.geometry) ch.geometry.dispose();
      if (ch.material) ch.material.dispose();
      zg.remove(ch);
    }
    zg.visible = showZip;
    if (!showZip) return;

    const trackMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const toothMat = new THREE.MeshBasicMaterial({ color: 0xffffff });

    // --- Seat zip: on the BACK FACE along the thickness midline ---
    // Back face of seat is the plane at z = -sB/2, spanning x ∈ [-sA/2, sA/2] and y ∈ [0, sD].
    // Zip runs along X at y = sD/2 (half-way up the thickness).
    const seatZipLen = sA - 0.04; // 2cm inset each side
    const seatZipY = sD / 2;
    const seatZipZ = -sB / 2 - 0.002; // nudged slightly outside the face
    const seatTrack = new THREE.Mesh(new THREE.BoxGeometry(seatZipLen, 0.003, 0.002), trackMat);
    seatTrack.position.set(0, seatZipY, seatZipZ);
    zg.add(seatTrack);
    const toothSpacing = 0.014;
    const nTeeth = Math.floor(seatZipLen / toothSpacing);
    for (let i = 0; i < nTeeth; i++) {
      const tx = -seatZipLen / 2 + (i + 0.5) * (seatZipLen / nTeeth);
      const side = (i % 2 === 0) ? 1 : -1;
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.004, 0.003), toothMat);
      tooth.position.set(tx, seatZipY + side * 0.004, seatZipZ);
      zg.add(tooth);
    }

    // --- Back cushion zip: on the BOTTOM FACE along the thickness midline ---
    // Back group origin is at the bottom-front corner of the back cushion.
    // Bottom face spans x ∈ [-sA/2, sA/2] and z ∈ [-sD, 0] in group-local space.
    // Zip runs along X at z = -sD/2 (half-way through thickness), y = 0 (bottom face).
    const backZipGroup = new THREE.Group();
    zg.add(backZipGroup);
    const tilt = (BACK_TILT_DEG * Math.PI) / 180;
    const gapDepth = GAP_BETWEEN_CM * SCALE;
    backZipGroup.position.set(0, sD - BACK_Y_DROP, -sB / 2 - gapDepth);
    backZipGroup.rotation.set(-tilt, 0, 0);

    const backZipLen = sA - 0.04;
    const backTrack = new THREE.Mesh(new THREE.BoxGeometry(backZipLen, 0.002, 0.003), trackMat);
    backTrack.position.set(0, -0.002, -sD / 2); // under the bottom face, at thickness midline
    backZipGroup.add(backTrack);
    for (let i = 0; i < nTeeth; i++) {
      const tx = -backZipLen / 2 + (i + 0.5) * (backZipLen / nTeeth);
      const side = (i % 2 === 0) ? 1 : -1;
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.003, 0.004), toothMat);
      tooth.position.set(tx, -0.002, -sD / 2 + side * 0.004);
      backZipGroup.add(tooth);
    }
  }, [sA, sB, sC, sD, showZip]);

  const handleReset = () => { resetCameraRef.current = true; };

  return (
    <div className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }} data-testid="hinged-cushion-3d-preview">
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-testid="three-canvas-container" />
      <div ref={el => dimLabelsRef.current.A = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0, transition: 'opacity 0.15s' }} data-testid="dim-label-A">
        {rawWidth > 0 ? `A ${unit === 'mm' ? rawWidth * 10 : rawWidth}${unit}` : `A ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.B = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0, transition: 'opacity 0.15s' }} data-testid="dim-label-B">
        {rawSeatDepth > 0 ? `B ${unit === 'mm' ? rawSeatDepth * 10 : rawSeatDepth}${unit}` : `B ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.C = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0, transition: 'opacity 0.15s' }} data-testid="dim-label-C">
        {rawBackHeight > 0 ? `C ${unit === 'mm' ? rawBackHeight * 10 : rawBackHeight}${unit}` : `C ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.D = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0, transition: 'opacity 0.15s' }} data-testid="dim-label-D">
        {rawThickness ? `D ${unit === 'mm' ? rawThickness * 10 : rawThickness}${unit}` : `D ${unit}`}
      </div>
      {showZip && (
        <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
          <svg width="24" height="6" viewBox="0 0 24 6"><line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" /></svg>
          <span className="font-medium">Zip placement</span>
        </div>
      )}
      <button onClick={handleReset} className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer" title="Reset view" data-testid="reset-view-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-15.36 6.36"/><path d="M3 12a9 9 0 0 1 15.36-6.36"/><polyline points="21 3 21 9 15 9"/><polyline points="3 21 3 15 9 15"/></svg>
      </button>
      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default HingedCushion3DPreview;
